import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { FiX, FiCalendar, FiRefreshCcw, FiUsers } from 'react-icons/fi';
import { fetchSchuljahre, fetchLernendeBySchuljahrId } from '../api';

// Spinner-Komponente
const Spinner = () => (
  <div style={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '40px',
    width: '100%'
  }}>
    <div style={{
      border: '4px solid #f3f3f3',
      borderTop: '4px solid #007acc',
      borderRadius: '50%',
      width: '32px',
      height: '32px',
      animation: 'spin 1s linear infinite'
    }} />
    <style>{`
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `}</style>
  </div>
);

const GlobalSyncModal = ({
  show,
  onClose,
  onConfirm
}) => {
  const [schuljahre, setSchuljahre] = useState([]);
  const [selectedSchuljahr, setSelectedSchuljahr] = useState('');
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState('');
  const [syncResult, setSyncResult] = useState(null);
  const [removeUnmatchedStudents, setRemoveUnmatchedStudents] = useState(false);

  useEffect(() => {
    if (show) {
      loadSchuljahre();
    }
  }, [show]);

  const loadSchuljahre = async () => {
    setLoading(true);
    setError('');
    
    try {
      const data = await fetchSchuljahre();
      
      if (data.error) {
        setError(data.error);
      } else {
        setSchuljahre(data);
        // Aktuelles Schuljahr als Standard auswählen
        const currentSchuljahr = data.find(jahr => jahr.isCurrent === 1);
        if (currentSchuljahr) {
          setSelectedSchuljahr(currentSchuljahr.SchuljahrID.toString());
        }
      }
    } catch (err) {
      setError('Fehler beim Laden der Schuljahre');
    } finally {
      setLoading(false);
    }
  };

  const handleSchuljahrChange = (event) => {
    setSelectedSchuljahr(event.target.value);
    setSyncResult(null); // Reset sync result when year changes
    setError('');
  };

  const handleRemoveUnmatchedChange = (event) => {
    setRemoveUnmatchedStudents(event.target.checked);
    setSyncResult(null); // Reset sync result when option changes
    setError('');
  };

  const handleSyncConfirm = async () => {
    if (!selectedSchuljahr) {
      setError('Bitte wählen Sie ein Schuljahr aus.');
      return;
    }

    setSyncing(true);
    setError('');
    setSyncResult(null);
    
    try {
      // Lade alle Lernenden für das ausgewählte Schuljahr
      const alleLernende = await fetchLernendeBySchuljahrId(selectedSchuljahr);
      
      if (alleLernende.error) {
        setError('Fehler beim Laden der Lernenden: ' + alleLernende.error);
        setSyncing(false);
        return;
      }

      // Führe die globale Synchronisation aus
      const result = await onConfirm(selectedSchuljahr, alleLernende, removeUnmatchedStudents);
      setSyncResult(result);
      
    } catch (error) {
      setError('Fehler bei der globalen Synchronisation: ' + error.message);
    } finally {
      setSyncing(false);
    }
  };

  const handleClose = () => {
    if (!syncing) {
      setSyncResult(null);
      setError('');
      onClose();
    }
  };

  const handleAcknowledgeResult = () => {
    setSyncResult(null);
    setError('');
    onClose();
  };

  if (!show) return null;

  return createPortal(
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000000
    }}>
      <div style={{
        backgroundColor: 'white',
        padding: '24px',
        borderRadius: '8px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        minWidth: '500px',
        maxWidth: '600px',
        position: 'relative',
        zIndex: 1000001
      }}>
        {/* Close Button */}
        <button
          onClick={handleClose}
          disabled={syncing}
          style={{
            position: 'absolute',
            top: '16px',
            right: '16px',
            background: 'none',
            border: 'none',
            cursor: syncing ? 'not-allowed' : 'pointer',
            padding: '4px',
            color: '#666',
            borderRadius: '4px',
            opacity: syncing ? 0.5 : 1
          }}
          title="Schließen"
        >
          <FiX size={20} />
        </button>

        {/* Modal Header */}
        <h3 style={{ 
          margin: '0 0 16px 0', 
          fontSize: '18px', 
          display: 'flex', 
          alignItems: 'center', 
          gap: '8px',
          paddingRight: '40px',
          color: '#333'
        }}>
          <FiUsers />
          Globale Lernenden-Synchronisation
        </h3>
        
        {/* Modal Content */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '20px',
          fontSize: '14px'
        }}>
          
          {/* Beschreibung */}
          <div style={{
            padding: '12px',
            backgroundColor: '#f8f9fa',
            borderRadius: '6px',
            border: '1px solid #dee2e6',
            color: '#666'
          }}>
            <p style={{ margin: 0, lineHeight: '1.5' }}>
              Diese Funktion synchronisiert <strong>alle Tasks</strong> im aktuellen Plan basierend auf den 
              in den Tasks hinterlegten Klassennamen. Für jeden Task, der eine Klassensynchronisation 
              definiert hat, werden die entsprechenden Lernenden automatisch zugewiesen. Optional können 
              auch Lernende entfernt werden, die nicht mehr in den definierten Klassen vorkommen.
            </p>
          </div>

          {/* Schuljahr-Auswahl */}
          <div>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              marginBottom: '8px'
            }}>
              <label style={{
                fontWeight: '500',
                color: '#333',
                whiteSpace: 'nowrap',
                display: 'flex',
                alignItems: 'center'
              }}>
                <FiCalendar style={{ marginRight: '8px' }} />
                Schuljahr für Synchronisation:
              </label>
            </div>
            
            {!loading && !error && (
              <select
                value={selectedSchuljahr}
                onChange={handleSchuljahrChange}
                disabled={syncing}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: '1px solid #ccc',
                  borderRadius: '6px',
                  fontSize: '14px',
                  backgroundColor: syncing ? '#f5f5f5' : 'white',
                  cursor: syncing ? 'not-allowed' : 'pointer',
                  opacity: syncing ? 0.7 : 1
                }}
              >
                <option value="">-- Schuljahr wählen --</option>
                {schuljahre.map((jahr) => (
                  <option key={jahr.SchuljahrID} value={jahr.SchuljahrID.toString()}>
                    {jahr.Name} 
                    {jahr.isCurrent === 1 && ' (Aktuell)'}
                  </option>
                ))}
              </select>
            )}
            
            {loading && (
              <div style={{ 
                padding: '12px',
                textAlign: 'center',
                color: '#666', 
                fontStyle: 'italic' 
              }}>
                Lade Schuljahre...
              </div>
            )}
          </div>

          {/* Checkbox für "Lernende auch entfernen" */}
          {selectedSchuljahr && !loading && (
            <div style={{
              padding: '12px',
              backgroundColor: '#f8f9fa',
              borderRadius: '6px',
              border: '1px solid #dee2e6'
            }}>
              <label style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '12px',
                cursor: syncing ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                lineHeight: '1.4'
              }}>
                <input
                  type="checkbox"
                  checked={removeUnmatchedStudents}
                  onChange={handleRemoveUnmatchedChange}
                  disabled={syncing}
                  style={{
                    marginTop: '2px',
                    cursor: syncing ? 'not-allowed' : 'pointer'
                  }}
                />
                <div>
                  <div style={{ fontWeight: '500', color: '#333', marginBottom: '4px' }}>
                    Lernende auch entfernen
                  </div>
                  <div style={{ color: '#666', fontSize: '13px' }}>
                    Entfernt Lernende aus Tasks, die <strong>nicht</strong> in den für den Task 
                    definierten Klassen vorkommen. Ohne diese Option werden nur neue Lernende hinzugefügt.
                  </div>
                </div>
              </label>
            </div>
          )}
          
          {/* Error Display */}
          {error && (
            <div style={{ 
              color: '#d32f2f', 
              backgroundColor: '#ffebee', 
              padding: '12px', 
              borderRadius: '6px',
              fontSize: '14px',
              border: '1px solid #ffcdd2'
            }}>
              <strong>Fehler:</strong> {error}
            </div>
          )}

          {/* Sync Result Display */}
          {syncResult && (
            <div style={{ 
              color: syncResult.success ? '#2e7d32' : '#d32f2f',
              backgroundColor: syncResult.success ? '#e8f5e8' : '#ffebee', 
              padding: '12px', 
              borderRadius: '6px',
              fontSize: '14px',
              border: syncResult.success ? '1px solid #c8e6c9' : '1px solid #ffcdd2'
            }}>
              <div style={{ fontWeight: '500', marginBottom: '8px' }}>
                {syncResult.success ? '✓ Synchronisation erfolgreich' : '✗ Synchronisation fehlgeschlagen'}
              </div>
              
              {syncResult.success && (
                <div style={{ lineHeight: '1.4' }}>
                  <div>Tasks synchronisiert: <strong>{syncResult.totalSyncedTasks}</strong></div>
                  <div>Lernende zugewiesen: <strong>{syncResult.totalAssignedStudents}</strong></div>
                  {syncResult.totalRemovedStudents > 0 && (
                    <div>Lernende entfernt: <strong>{syncResult.totalRemovedStudents}</strong></div>
                  )}
                  {syncResult.tasksWithoutSync > 0 && (
                    <div style={{ marginTop: '4px', fontSize: '13px', opacity: 0.8 }}>
                      ({syncResult.tasksWithoutSync} Tasks ohne Klassensynchronisation übersprungen)
                    </div>
                  )}
                </div>
              )}

              {syncResult.message && (
                <div style={{ 
                  marginTop: '8px', 
                  fontSize: '13px',
                  fontStyle: 'italic'
                }}>
                  {syncResult.message}
                </div>
              )}


            </div>
          )}

          {/* Syncing Display */}
          {syncing && (
            <div style={{
              padding: '20px',
              textAlign: 'center',
              backgroundColor: '#f0f8ff',
              borderRadius: '6px',
              border: '1px solid #b8daff'
            }}>
              <Spinner />
              <div style={{ 
                marginTop: '12px',
                color: '#0c5460',
                fontWeight: '500'
              }}>
                Synchronisiere alle Tasks...
              </div>
              <div style={{ 
                marginTop: '4px',
                fontSize: '12px',
                color: '#666',
                fontStyle: 'italic'
              }}>
                Dies kann einen Moment dauern.
              </div>
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div style={{ 
          display: 'flex', 
          gap: '12px', 
          justifyContent: syncResult ? 'flex-end' : 'space-between',
          marginTop: '24px',
          paddingTop: '16px',
          borderTop: '1px solid #eee',
          alignItems: 'center'
        }}>
          {/* Sync Button - nur anzeigen wenn noch kein Result vorhanden */}
          {!syncResult && (
            <button
              onClick={handleSyncConfirm}
              disabled={!selectedSchuljahr || loading || syncing}
              style={{
                padding: '12px 24px',
                border: 'none',
                borderRadius: '6px',
                backgroundColor: (!selectedSchuljahr || loading || syncing) ? '#ccc' : '#28a745',
                color: 'white',
                cursor: (!selectedSchuljahr || loading || syncing) ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                minWidth: '180px',
                justifyContent: 'center'
              }}
              title={
                !selectedSchuljahr ? 'Wählen Sie ein Schuljahr aus' :
                loading ? 'Lade Daten...' :
                syncing ? 'Synchronisation läuft...' :
                'Starte globale Synchronisation aller Tasks'
              }
            >
              <FiRefreshCcw 
                size={16} 
                style={{
                  animation: syncing ? 'spin 1s linear infinite' : 'none'
                }}
              />
              {syncing ? 'Synchronisiere...' : 'Lernende synchronisieren'}
            </button>
          )}

          {/* Close/OK Button */}
          <button
            onClick={syncResult ? handleAcknowledgeResult : handleClose}
            disabled={syncing}
            style={{
              padding: '12px 24px',
              border: syncResult ? 'none' : '1px solid #ccc',
              borderRadius: '6px',
              backgroundColor: syncResult ? (syncResult.success ? '#28a745' : '#dc3545') : 'white',
              color: syncResult ? 'white' : (syncing ? '#999' : '#333'),
              cursor: syncing ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: syncResult ? '500' : 'normal',
              opacity: syncing ? 0.6 : 1,
              minWidth: syncResult ? '100px' : 'auto'
            }}
          >
            {syncing ? 'Bitte warten...' : (syncResult ? 'OK' : 'Schließen')}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default GlobalSyncModal;